import Vue from 'vue';
import validate from 'mixins/validate';
import phone from 'mixins/phone-validation';
import signups from 'mixins/signups';
import formGlobalErrors from 'mixins/form-global-errors';
import 'lib/vue-phone-input.js';
import { decode } from 'utils/general';
import VueScrollTo from 'vue-scrollto';
import { pushDataSignup } from 'lib/tracking';

Vue.use(VueScrollTo);

const ATTENTIVE_CREATIVE_ID = '66764';

export default {
  components: {
    'select-input': () => import('../select-input/select-input.vue'),
    checkbox: () => import('../checkbox/checkbox.vue'),
  },
  mixins: [validate, phone, signups, formGlobalErrors],
  props: ['formId', 'klaviyoListId', 'baseUrl', 'months'],
  data() {
    return {
      firstNameValue: '',
      lastNameValue: '',
      emailValue: '',
      passwordValue: '',
      acceptMarketing: false,
      countryValue: '',
      birthdayRequired: false,
      birthday: [],
    };
  },
  methods: {
    submitButtonState() {
      const showErrors = false;
      const isButtonActive = this.validateCustomForm(showErrors);
      const btn = this.$refs['submitButton'];

      isButtonActive
        ? btn.classList.remove('is-disabled')
        : btn.classList.add('is-disabled');
    },
    phoneOnChange() {
      this.phoneValidation(true);
      this.submitButtonState();
    },
    generateCountryArray() {
      return Object.keys(Countries).map((label) => ({
        label,
        value: label,
      }));
    },
    validateCustomForm(showErrors = true) {
      const checkPhone = this.phoneValidation();
      const countrySelectDropdown = this.$refs['countrySelect'];
      const daysDropdown = this.$refs['daysDropdown'];
      const monthsDropdown = this.$refs['monthsDropdown'];

      let isValid = this.$children
        .filter((c) => typeof c.validateInput !== 'undefined')
        .map((c) => {
          const validInput = c.validateInput();
          if (showErrors) {
            this.highlightErrors(c, validInput);
          }

          return c.validateInput();
        })
        .every((res) => res);

      // check if country region select is not empty
      if (
        countrySelectDropdown &&
        countrySelectDropdown.inputValue === 'placeholder'
      ) {
        if (showErrors) {
          countrySelectDropdown.isValid = false;
        }
        isValid = false;
      }

      // check if birthday is required
      if (this.birthdayRequired) {
        if (daysDropdown.inputValue === 'placeholder') {
          if (showErrors) {
            daysDropdown.isValid = false;
          }

          isValid = false;
        }

        if (monthsDropdown.inputValue === 'placeholder') {
          if (showErrors) {
            monthsDropdown.isValid = false;
          }
          isValid = false;
        }
      }

      // check if user added a phone and if its valid
      if (!checkPhone) {
        isValid = false;

        if (showErrors) {
          this.phoneValidation(true);
        }
      }

      return isValid;
    },
    saveCustomer() {
      const isValidForm = this.validateCustomForm();
      const countryVal =
        this.countryValue === 'placeholder' ? '' : this.countryValue;
      const customerSaveOptions = {
        login: true,
        redirect: true,
        redirectUrl: '/account',
        formId: this.formId,
        data: {
          first_name: this.firstNameValue,
          last_name: this.lastNameValue,
          email: this.emailValue,
          password: this.passwordValue,
          password_confirmation: this.passwordValue,
          accepts_marketing: this.acceptMarketing,
          birthday_day_int:
            this.birthday[1] === 'placeholder' ? '' : this.birthday[1],
          birthday_month:
            this.birthday[0] === 'placeholder' ? '' : this.birthday[0],
          default_address: {
            country: countryVal,
          },
          phone: this.phoneValue,
        },
      };

      if (isValidForm) {
        this.showLoader('show');
        let sanitizeBirthday = '';
        if (this.birthday.every((val) => val !== 'placeholder')) {
          sanitizeBirthday = this.birthday.join('/');
        }
        this.submitToKlaviyo({
          listId: this.klaviyoListId,
          email: this.emailValue,
          $first_name: this.firstNameValue,
          $last_name: this.lastNameValue,
          acceptMarketing: this.acceptMarketing,
          birthday: sanitizeBirthday,
        })
          .then(() => {
            this.submitToAttentive(ATTENTIVE_CREATIVE_ID);
          })
          .then(() => {
            this.submitToCF(customerSaveOptions);
          });
      }
    },
    onInputChange({ name, value }) {
      const match = /\[(\w*)\]/.exec(name);
      const countrySelectDropdown = this.$refs['countrySelect'];
      const daysDropdown = this.$refs['daysDropdown'];
      const monthsDropdown = this.$refs['monthsDropdown'];

      if (!match || !match[1]) {
        return false;
      }

      if (
        (match[1] === 'Day' || match[1] === 'Month') &&
        value !== 'placeholder'
      ) {
        this.birthdayRequired = true;
      }

      switch (match[1]) {
        case 'first_name':
          this.firstNameValue = value;
          break;
        case 'last_name':
          this.lastNameValue = value;
          break;
        case 'email':
          this.emailValue = value;
          break;
        case 'password':
          this.passwordValue = value;
          break;
        case 'sms':
          this.phoneSendSMS = !!value;
          break;
        case 'accepts_marketing':
          this.acceptMarketing = !!value;
          break;
        case 'country':
          countrySelectDropdown.isValid = true;
          this.countryValue = value;
          break;
        case 'Month':
          monthsDropdown.isValid = true;
          this.birthday[0] = value;
          break;
        case 'Day':
          daysDropdown.isValid = true;
          this.birthday[1] = value;
          break;
        default:
          break;
      }

      this.phoneValidation(true);
      this.submitButtonState();
    },
    submitToCF(customerSaveOptions) {
      // Clear global errors
      this.globalErrors = '';

      CF.customer
        .save(customerSaveOptions)
        .then((response) => {
          // updates first name, last name, email and phone
          if (response) {
            this.displayResponseErrors(response);
            this.$scrollTo(document.body);
          } else if (
            typeof response === 'undefined' &&
            CF.customer.data.state === 'disabled'
          ) {
            this.displayResponseErrors(CF.customer.conditions);
            this.globalErrors +=
              '. ' +
              this.$t('customer.register.sent_email', {
                email: CF.customer.data.email,
              });
            this.$scrollTo(document.body);
          } else {
            const param = {
              event: 'sign_up',
              method: 'email',
              country:
                CF.customer.data?.default_address?.country?.toLowerCase(),
              shopify_module_name: 'account register form'
            };
            pushDataSignup(param);
          }

          this.showLoader('hide');
        })
        .catch(function (err) {
          console.error('Could not save customer', err);
          this.showLoader('hide');
        });
    },
    showLoader(show = 'show') {
      const element = this.$el.querySelector('.register-form');
      if (show === 'show') {
        element.classList.add('is-loading');
      } else {
        element.classList.remove('is-loading');
      }
    },
    generateMonths() {
      const arr = JSON.parse(decode(this.months));

      return Object.keys(arr).map((label) => ({
        label,
        value: label,
      }));
    },
    generateDays() {
      const arr = [];
      const maxDays = 31;

      for (let index = 1; index <= maxDays; index++) {
        const obj = {
          label: index,
          value: index,
        };

        arr.push(obj);
      }

      return arr;
    },
  },
};
